import React, { useState } from 'react';
import Login from './Login';
import { sendForgotPasswordLink, changePassword, postLoginUser } from './authApi';
import { useAppDispatch } from '../../app/store';
import { useNavigate } from 'react-router-dom';
import { IChangePassword, IForgotPasswordLink, ILoginRequest } from '../../models/loginRequest';
import { useAuth } from '../../context/AuthContext';
import { LoginType } from '../../models/types';
import { LOGIN_TYPES } from '../../utils/constant';
import { useTranslation } from 'react-i18next';
import ModalComponent from '../../components/Modal';

const LoginContainer: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [resetForm, setResetForm] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loginType, setLoginType] = useState<LoginType>(LOGIN_TYPES.LOGIN);
    const [operationSuccessMessages, setOperationSuccessMessage] = useState({ title: '', description: '' });
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t } = useTranslation();

    //Setup token on successfully acquiring valid token 
    const updateValidToken = (response: any) => {
        const { access_token, expires_in } = response;
        login(access_token, parseInt(expires_in));
    };

    //Handle click on login submit event
    const handleLogin = (values: any) => {
        const loginRequest: ILoginRequest = {
            email: values.email,
            password: values.password
        }

        //Trigger user login api request
        dispatch(postLoginUser(loginRequest))
            .unwrap()
            .then((response) => {
                setLoading(false);
                if (response?.status && response?.status === 'success' && response?.data?.access_token && response?.data?.user?.login_cnt === 1) {
                    updateValidToken(response.data);
                    setLoginType(LOGIN_TYPES.CHANGE_PASSWORD);
                }
                else if (response?.status && response?.status === 'success' && response?.data?.access_token) {
                    updateValidToken(response.data);
                    navigate('/home', { replace: true });
                }
            })
            .catch((error) => {
                setLoading(false);
                console.error('Login failed:', error);
            });
    }

    //Handle click on send reset link event
    const handleForgotPassword = (values: any) => {
        const forgotPassword: IForgotPasswordLink = {
            email: values.email
        };
        dispatch(sendForgotPasswordLink(forgotPassword))
            .unwrap()
            .then((response) => {
                setLoading(false);
                if (response?.status && response?.status === 'success') {
                    setOperationSuccessMessage({
                        title: t('success'),
                        description: t('send-reset-link.response.success.desc')
                    });
                    setIsModalVisible(true);
                }
            });
    };

    //Handle click event on send resent link event response modal continue button
    const handleModalOk = () => {
        setLoading(false);
        setIsModalVisible(false);
        setOperationSuccessMessage({ title: '', description: '' });
        setLoginType(LOGIN_TYPES.LOGIN);
        setResetForm(true);
    };

    const backToLogin = () => {
        setLoginType(LOGIN_TYPES.LOGIN);
        setResetForm(true);
    }
    //Handle click event from change password button click
    const handleChangePassword = (values: any) => {
        const changePasswordRequest: IChangePassword = {
            email: values.email,
            old_password: values.currentPassword,
            password: values.newPassword,
            password_confirmation: values.confirmPassword
        };

        //Trigger api call for change password
        dispatch(changePassword(changePasswordRequest))
            .unwrap()
            .then((response) => {
                setLoading(false);
                if (response?.status === 'success') {
                    setOperationSuccessMessage({
                        title: t('change-password.response.success.title'),
                        description: t('change-password.response.success.desc')
                    });
                    setIsModalVisible(true);
                }
            })
            .catch((error) => {
                setLoading(false);
                console.error('Failed to change password:', error);
            });
    };

    //Handle click on submit button event
    const onFinish = (values: any) => {
        setLoading(true);

        switch (loginType) {
            case LOGIN_TYPES.LOGIN: handleLogin(values); break;
            case LOGIN_TYPES.FORGOT_PASSWORD: handleForgotPassword(values); break;
            case LOGIN_TYPES.CHANGE_PASSWORD: handleChangePassword(values); break;
        }
    };

    return (
        <>
            <Login
                loading={loading}
                onFinish={onFinish}
                loginType={loginType}
                onForgotPassword={(values) => setLoginType(values)}
                resetForm={resetForm} 
                backToLogin={backToLogin}
                />

            <ModalComponent
                title={operationSuccessMessages.title}
                visible={isModalVisible}
                okButtonText={t('btn.continue')}
                onOk={handleModalOk}
                onCancel={handleModalOk}
                // onCancel={() => { setIsModalVisible(false); setLoading(false); }}
                description={operationSuccessMessages.description} />
        </>
    );
};

export default LoginContainer;